import { useCallback, useMemo } from 'react'

import {
  type Brand,
  type PaginatedBrandsResult,
} from '@redteclab/api/clients/bully'

import { useMarketplacePartnerRegisterPageFormApiGetBrands } from '../api/swr/useMarketplacePartnerRegisterPageFormApiGetBrands'

type UseMarketplacePartnerRegisterPageBrandsReturn = {
  allBrands: Brand[]
  hasMore: boolean
  isLoading: boolean
  loadMore: () => void
}

export const useMarketplacePartnerRegisterPageBrands =
  (): UseMarketplacePartnerRegisterPageBrandsReturn => {
    const LIMIT = 100

    const { data, isLoading, isValidating, setSize, size } =
      useMarketplacePartnerRegisterPageFormApiGetBrands(LIMIT)

    const allBrands = useMemo(() => {
      if (!data) {
        return []
      }

      return data.flatMap((page: PaginatedBrandsResult) => page.items)
    }, [data])

    const lastPage = data?.at(-1)
    const hasMore = Boolean(lastPage?.hasNext)
    const isFirstPageLoaded = Boolean(data && data.length > 0)

    const loadMore = useCallback(() => {
      if (isValidating || !hasMore) {
        return
      }
      const loadedPages = data?.length ?? 0
      if (loadedPages < size) {
        return
      }
      void setSize(size + 1)
    }, [data, hasMore, isValidating, setSize, size])

    return {
      allBrands,
      hasMore,
      isLoading: isLoading && !isFirstPageLoaded,
      loadMore,
    }
  }
